import React from "react";
import {
  apiImportBarangDryRun, apiImportBarangApply, apiImportBarangKembalikan, apiImportBarangRiwayat, apiUnduhBarang,
  formatApiErrorDetail,
} from "@/lib/api";
import { filterList, jumlahTerapkan, labelField, nilai, saringBaris, STATUS_LABEL } from "@/lib/imporBarang";
import * as W from "@/lib/waktu";
import { CheckCircle2, Download, FileSpreadsheet, History, Loader2, Upload } from "lucide-react";

const errText = (e) => formatApiErrorDetail(e.response?.data?.detail) || e.message;
const STATUS_STYLE = {
  DIBUAT: "border-emerald-500/30 bg-emerald-500/10 text-emerald-300",
  DIUBAH: "border-cyan-500/30 bg-cyan-500/10 text-cyan-300",
  SAMA: "border-slate-600 bg-slate-700/30 text-slate-400",
  BENTROK: "border-orange-500/40 bg-orange-500/10 text-orange-300",
  BERMASALAH: "border-red-500/30 bg-red-500/10 text-red-300",
  DILEWATI: "border-red-500/30 bg-red-500/10 text-red-300",
};
const tombol = "inline-flex items-center gap-2 rounded-lg border border-slate-600 px-4 py-2 text-sm text-slate-200 hover:border-amber-500/50 disabled:opacity-50";

// owner 2026-10-12: Download (Owner + Manager; the Manager's file has no HPP) next to Import.
async function unduh(setBusy, setError) {
  setBusy("unduh"); setError(null);
  try {
    const { blob, nama } = await apiUnduhBarang();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = nama;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch (e) { setError(errText(e)); } finally { setBusy(null); }
}

function Baris({ b }) {
  return (
    <div data-testid={`import-row-${b.kode}`} className="rounded-lg border border-slate-800 bg-white px-3 py-2 text-xs">
      <div className="flex flex-wrap items-center gap-2">
        <span className={`rounded border px-1.5 py-0.5 font-mono ${STATUS_STYLE[b.status]}`}>{STATUS_LABEL[b.status] || b.status}</span>
        <span className="font-mono text-amber-300">{b.kode}</span>
        <span className="text-slate-200">{b.nama}</span>
        <span className="ml-auto font-mono text-slate-500">baris {b.baris}</span>
      </div>
      <div className="mt-1 space-y-0.5 text-slate-300">
        {b.alasan.map((a) => <div key={a} className="text-red-300">✕ {a}</div>)}
        {["DIBUAT", "DIUBAH"].includes(b.status) && b.perlu_dicek.map((a) => <div key={a} className="text-orange-300">● Perlu dicek: {a}</div>)}
        {b.perubahan && Object.entries(b.perubahan).map(([f, [lama, baru]]) => (
          <div key={f} className="text-cyan-300"><b>{labelField(f)}</b>: {nilai(f, lama)} → {nilai(f, baru)}</div>
        ))}
        {b.peringatan.map((w) => <div key={w} className="text-amber-300">⚠ {w}</div>)}
      </div>
    </div>
  );
}

function Riwayat({ onChanged }) {
  const [rows, setRows] = React.useState(null);
  const [pilih, setPilih] = React.useState(null);
  const [alasan, setAlasan] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [msg, setMsg] = React.useState(null);
  const load = React.useCallback(() => apiImportBarangRiwayat().then(setRows).catch((e) => setMsg({ ok: false, t: errText(e) })), []);
  React.useEffect(() => { load(); }, [load]);
  const kembalikan = async (r) => {
    if (!window.confirm(`Kembalikan impor "${r.file}"? Barang yang diubah impor ini kembali ke nilai sebelum impor; barang baru dari impor ini dinonaktifkan. Barang yang sudah diubah lagi sesudahnya tidak disentuh.`)) return;
    setBusy(true); setMsg(null);
    try {
      const h = await apiImportBarangKembalikan(r.id, alasan.trim());
      setMsg({ ok: true, t: `Dikembalikan: ${h.dikembalikan} barang, ${h.dinonaktifkan} barang baru dinonaktifkan${h.dilewati.length ? `, ${h.dilewati.length} dilewati (${h.dilewati.map((x) => `${x.kode}: ${x.alasan}`).join("; ")})` : ""}.` });
      setPilih(null); setAlasan(""); load(); onChanged?.();
    } catch (e) { setMsg({ ok: false, t: errText(e) }); } finally { setBusy(false); }
  };
  return (
    <div data-testid="import-riwayat" className="space-y-2">
      <p className="text-sm font-semibold text-soq-biru">Riwayat impor (Owner)</p>
      {msg && <div className={`rounded-lg px-3 py-2 text-sm ${msg.ok ? "bg-emerald-500/10 text-emerald-300" : "bg-red-500/10 text-red-300"}`}>{msg.t}</div>}
      {!rows && <Loader2 className="animate-spin text-slate-400" size={16} />}
      {rows && rows.length === 0 && <p className="text-xs text-slate-500">Belum ada impor yang diterapkan.</p>}
      {(rows || []).map((r) => (
        <div key={r.id} data-testid={`riwayat-${r.id}`} className="rounded-lg border border-slate-800 bg-white px-3 py-2 text-xs">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-slate-200">{r.file || "(tanpa nama)"}</span>
            <span className="text-slate-500">{r.oleh} · {W.waktu(r.selesai)} · format {r.format === "BARU" ? "unduhan" : "lama"}</span>
            <span className="text-slate-300">{r.dibuat} baru, {r.diubah} diubah</span>
            {r.status === "DIKEMBALIKAN" && <span className="rounded border border-slate-500 px-1.5 text-slate-400">DIKEMBALIKAN</span>}
            {r.bisa_dikembalikan && pilih !== r.id && (
              <button data-testid={`kembalikan-${r.id}`} onClick={() => { setPilih(r.id); setAlasan(""); }} className="ml-auto rounded-md border border-slate-600 px-2 py-1 text-slate-200">Kembalikan impor</button>
            )}
          </div>
          {r.kembalikan && <p className="mt-1 text-slate-500">Dikembalikan {r.kembalikan.oleh} · {W.waktu(r.kembalikan.pada)}: {r.kembalikan.alasan}</p>}
          {pilih === r.id && (
            <div className="mt-2 flex flex-wrap gap-2">
              <input data-testid="kembalikan-alasan" value={alasan} onChange={(e) => setAlasan(e.target.value)} placeholder="Alasan (wajib, min. 5 huruf)"
                className="min-h-[36px] flex-1 rounded-md border border-slate-700 bg-slate-950 px-2 text-sm text-slate-100" />
              <button data-testid="kembalikan-kirim" disabled={busy || alasan.trim().length < 5} onClick={() => kembalikan(r)}
                className="rounded-md bg-soq-biru px-3 py-1.5 text-sm text-white disabled:opacity-40">{busy ? "…" : "Kembalikan"}</button>
              <button onClick={() => setPilih(null)} className="text-slate-400">Batal</button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// F1.5-C + owner 2026-10-12: upload, check the dry-run (nothing is changed), then apply exactly that
// result. The file is read by the server in memory and never stored; every row is validated there.
export default function ImportBarangPanel({ onApplied, owner }) {
  const [open, setOpen] = React.useState(false);
  const [file, setFile] = React.useState(null);
  const [hasil, setHasil] = React.useState(null);
  const [filter, setFilter] = React.useState("PENTING");
  const [busy, setBusy] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [selesai, setSelesai] = React.useState(null);
  const [riwayat, setRiwayat] = React.useState(false);

  const dryRun = async () => {
    if (!file) return;
    setBusy("dry"); setError(null); setSelesai(null); setHasil(null); setFilter("PENTING");
    try { setHasil(await apiImportBarangDryRun(file)); } catch (e) { setError(errText(e)); } finally { setBusy(null); }
  };
  const apply = async () => {
    setBusy("apply"); setError(null);
    try { setSelesai(await apiImportBarangApply(hasil.token)); setHasil(null); setFile(null); onApplied?.(); }
    catch (e) { setError(errText(e)); } finally { setBusy(null); }
  };

  const r = hasil?.ringkasan;
  const rows = hasil ? saringBaris(hasil.baris, filter) : [];
  const n = jumlahTerapkan(r);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <button data-testid="barang-unduh" onClick={() => unduh(setBusy, setError)} disabled={!!busy} className={tombol}>
          {busy === "unduh" ? <Loader2 className="animate-spin" size={16} /> : <Download size={16} />} Download Excel
        </button>
        {!open && (
          <button data-testid="import-open" onClick={() => setOpen(true)} className={tombol}>
            <FileSpreadsheet size={16} /> Import dari Excel
          </button>
        )}
        {owner && (
          <button data-testid="import-riwayat-open" onClick={() => setRiwayat(!riwayat)} className={tombol}>
            <History size={16} /> Riwayat impor
          </button>
        )}
      </div>
      {!open && error && <div data-testid="import-error" className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2.5 text-sm text-red-300">{error}</div>}
      {riwayat && owner && <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-4"><Riwayat onChanged={onApplied} /></div>}
      {open && (
        <div data-testid="import-panel" className="rounded-xl border border-amber-500/30 bg-slate-900/60 p-4 sm:p-5 space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-[15px] font-bold text-soq-biru">Import master barang</span>
            <input data-testid="import-file" type="file" accept=".xlsx" onChange={(e) => { setFile(e.target.files?.[0] || null); setHasil(null); }} className="max-w-full text-sm text-slate-300" />
            <button data-testid="import-dry-run" onClick={dryRun} disabled={!file || !!busy} className={tombol}>
              {busy === "dry" ? <Loader2 className="animate-spin" size={15} /> : <Upload size={15} />} Periksa
            </button>
            <button onClick={() => setOpen(false)} className="ml-auto text-xs text-slate-400 hover:text-slate-200">Tutup</button>
          </div>
          <p className="text-xs text-slate-500">
            Pakai file dari <b>Download Excel</b>: ubah atau tambah baris (kode kosong = barang baru, kode dibuat otomatis), lalu Periksa. Pemeriksaan <b>tidak mengubah data</b>; perubahan baru terjadi setelah <b>Terapkan</b>. Impor tidak pernah menghapus barang. Baris <b>Bentrok</b> (barang diubah di aplikasi setelah file diunduh) dan <b>Bermasalah</b> tidak diterapkan. Jenis mesin & mode harga hanya info.
            {owner
              ? <> Sebelum diterapkan data lama dibackup; bisa dikembalikan lewat Riwayat impor. File format lama (sheet <b>Master Barang</b> + <b>Harga Strata</b>) tetap bisa dipakai Owner.</>
              : <> Manager: harga jual tidak bisa diubah; barang baru disimpan <b>nonaktif</b> sampai Owner mengisi harga dan mengaktifkan.</>}
          </p>
          {error && <div data-testid="import-error" className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2.5 text-sm text-red-300">{error}</div>}
          {selesai && (
            <div data-testid="import-selesai" className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-4 py-2.5 text-sm text-emerald-300">
              <CheckCircle2 size={14} className="inline mr-1" /> Diterapkan: {selesai.dibuat} baru{selesai.kode_baru?.length ? ` (${selesai.kode_baru.join(", ")})` : ""}, {selesai.diubah} diubah, {selesai.perlu_dicek} ditandai perlu dicek{selesai.dilewati.length ? `, ${selesai.dilewati.length} dilewati (${selesai.dilewati.map((x) => `${x.kode}: ${x.alasan}`).join("; ")})` : ""}.
            </div>
          )}
          {hasil && (
            <>
              <div className="flex flex-wrap items-center gap-2 text-sm" data-testid="import-ringkasan">
                {filterList(r).map((f) => (
                  <button key={f.key} data-testid={`import-filter-${f.key}`} onClick={() => setFilter(f.key)}
                    className={`rounded-md border px-2.5 py-1 text-xs ${filter === f.key ? "border-amber-500/60 text-amber-300" : "border-slate-700 text-slate-400"}`}>
                    {f.label}{f.n !== null ? ` ${f.n}` : ""}
                  </button>
                ))}
                <button data-testid="import-apply" onClick={apply} disabled={!!busy || n === 0}
                  className="w-full sm:w-auto sm:ml-auto inline-flex justify-center items-center gap-2 rounded-lg bg-amber-500 px-4 py-2 text-sm font-semibold text-slate-950 hover:bg-amber-400 disabled:opacity-50">
                  {busy === "apply" ? <Loader2 className="animate-spin" size={15} /> : null} Terapkan {n} barang
                </button>
              </div>
              {hasil.catatan.length > 0 && <ul className="text-xs text-slate-400 list-disc pl-5">{hasil.catatan.map((c) => <li key={c}>{c}</li>)}</ul>}
              <div className="max-h-[32rem] overflow-y-auto space-y-2" data-testid="import-baris">
                {rows.length === 0 && <p className="text-xs text-slate-500">Tidak ada baris di tampilan ini.</p>}
                {rows.map((b) => <Baris key={`${b.baris}-${b.kode}`} b={b} />)}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
